import React, {useEffect} from 'react'
import PropTypes from 'prop-types'
import {Box, Text, Flex, Link} from '@chakra-ui/react'
import {WarningIcon} from '@chakra-ui/icons'
import {useTranslation} from 'react-i18next'
import {createSetupVerificationInvoice} from '../../../../api'
import {ReceiptIcon} from '../../../icons'
import {useRails} from '../../../contexts/rails'

export const stepName = 'updateInvoiceTemplate'

const calculateStatus = ({marmaladeInvoiceSetupStatus, submittedRequest}) => {
  if (marmaladeInvoiceSetupStatus === 'verified') {
    return 'completed'
  }
  if (submittedRequest || marmaladeInvoiceSetupStatus === 'pending') {
    return 'reviewing'
  }

  // A declined status is treated as incomplete so the supplier can resubmit
  // once the issue is resolved.
  return 'incomplete'
}

const DeclinedNotification = ({accountingProviderName}) => {
  const {t} = useTranslation('cashinStepper')
  const howToUpdateUrl =
    accountingProviderName === 'Xero'
      ? 'https://marmalade.ladesk.com/263421-Create-a-Marmalade-Branding-Theme'
      : 'https://marmalade.ladesk.com/494873-Quickbooks---Update-Invoice-Form-Style'

  return (
    <Flex alignItems="start">
      <WarningIcon boxSize="13px" color="red.600" mt="xxs" mr="xxs" />
      <Box>
        <Text textStyle="body-small">
          {t('steps.updateInvoiceTemplate.declined.message')}
        </Text>
        <Text textStyle="body-small">
          {t('steps.updateInvoiceTemplate.declined.unsure')}{' '}
          <Link
            href={howToUpdateUrl}
            isExternal
            target="_blank"
            color="mld.primary.100"
          >
            {t('steps.updateInvoiceTemplate.declined.helpArticle')}
          </Link>
        </Text>
      </Box>
    </Flex>
  )
}

DeclinedNotification.propTypes = {
  accountingProviderName: PropTypes.string.isRequired,
}

const useNotification = ({accountingProvider, marmaladeInvoiceSetupStatus}) => {
  if (marmaladeInvoiceSetupStatus === 'declined') {
    return (
      <DeclinedNotification accountingProviderName={accountingProvider.name} />
    )
  }

  return undefined
}

export default (stepperData) => {
  const {t} = useTranslation('cashinStepper')
  const {authenticityToken, accountingProvider} = useRails()

  const [submittedRequest, setSubmittedRequest] = React.useState(false)
  const prerequisiteStepsCompleted =
    stepperData.verificationStatus === 'verified' &&
    stepperData.supplierAgreementAccepted &&
    stepperData.payoutAccountConnected

  const onClick = async () => {
    await createSetupVerificationInvoice({
      supplierId: stepperData.supplierId,
      authenticityToken,
    })
    setSubmittedRequest(true)
  }
  const status = calculateStatus({...stepperData, submittedRequest})

  useEffect(() => {
    // Reset the submitting override state when the status is changed
    setSubmittedRequest(false)
  }, [stepperData.marmaladeInvoiceSetupStatus])

  return {
    name: stepName,
    visible: !stepperData.features.invitePayersToMarmalade,
    status,
    active: status === 'incomplete' && prerequisiteStepsCompleted,
    icon: <ReceiptIcon />,
    title: t('steps.updateInvoiceTemplate.title'),
    subtitle: {default: t('steps.updateInvoiceTemplate.subtitle')},
    notification: useNotification({
      accountingProvider,
      marmaladeInvoiceSetupStatus: stepperData.marmaladeInvoiceSetupStatus,
    }),
    action: {
      label: t('steps.updateInvoiceTemplate.actionLabel'),
      onClick,
    },
  }
}
